import type { FileValidationOptions, FileValidationError } from './types';

/**
 * Default validation options for image uploads
 */
export const DEFAULT_VALIDATION_OPTIONS: FileValidationOptions = {
  maxSizeMB: 10,
  allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'application/pdf'],
};

/**
 * Validate a single file
 */
export const validateFile = (
  file: File,
  options: FileValidationOptions = DEFAULT_VALIDATION_OPTIONS
): string | null => {
  const maxSizeBytes = options.maxSizeMB * 1024 * 1024;

  if (!options.allowedTypes.includes(file.type)) {
    const allowedExtensions = options.allowedTypes
      .map((type) => {
        switch (type) {
          case 'image/jpeg':
            return 'JPG';
          case 'image/png':
            return 'PNG';
          case 'image/gif':
            return 'GIF';
          case 'application/pdf':
            return 'PDF';
          default:
            return type.split('/')[1]?.toUpperCase() || type;
        }
      })
      .join(', ');
    return `File type not allowed. Allowed types: ${allowedExtensions}`;
  }

  if (file.size > maxSizeBytes) {
    return `File too large (max ${options.maxSizeMB}MB)`;
  }

  return null;
};

/**
 * Validate multiple files and return any errors
 */
export const validateFiles = (
  files: File[],
  options: FileValidationOptions = DEFAULT_VALIDATION_OPTIONS
): FileValidationError[] => {
  const errors: FileValidationError[] = [];

  for (const file of files) {
    const error = validateFile(file, options);
    if (error) {
      errors.push({ file, error });
    }
  }

  return errors;
};

/**
 * Check if a file is an image
 */
export const isImage = (mimeType: string | undefined | null): boolean => {
  return mimeType?.startsWith('image/') ?? false;
};

/**
 * Check if a file is a PDF
 */
export const isPdf = (mimeType: string | undefined | null): boolean => {
  return mimeType === 'application/pdf';
};
